/**
 * A VIDEO THE AUTHOR CANNOT CHANGE (v1.0.29) - with a person at MoonX, scheduled, published or
 * taken down - shown rather than edited, as the article editor shows such an article
 * (../articles/editor/ArticleReadView.tsx): the video and its words, the facts, and an editor's
 * notes where there are any. A published video ends with "Archive this video", as a published
 * article's page does: the list's cards offer no Archive, and a published piece is never deleted.
 * A live stream (1.2.0) says when it is planned to start and end, on the author's clock.
 */
import { Stack, Text, Title } from '@mantine/core';
import type {
  EditorCategory,
  ReviewRecord,
  WorkingStream,
} from '../../features/cabinet/api/editor-api';
import { formatInstant } from '../../features/cabinet/format';
import { StatusPill } from '../../features/cabinet/components';
import { InAppAnchor } from '../../features/shell/InAppAnchor';
import { emulatorImageUrl, servedUrlOnError } from '../../features/storage/emulator-image-url';
import { useTranslation } from '../../i18n';
import { ArchiveThisArticle } from '../articles/editor/ArchiveThisArticle';
import { ReadOnlyText } from '../articles/editor/ReadOnlyText';
import { ReviewNotes } from '../articles/editor/ReviewNotes';
import type { DraftBase, EditorDraft } from '../articles/editor/document';
import editorClasses from '../articles/editor/editor.module.css';
import { VideoBlock } from './VideoBlock';
import { VIDEOS_HREF } from './paths';
import type { ArticleVideoState } from './use-article-video';
import classes from './video.module.css';

export interface VideoReadViewProps {
  readonly base: DraftBase;
  readonly draft: EditorDraft;
  readonly video: ArticleVideoState;
  /** A live stream's planned times; null for a video that is not one. */
  readonly stream?: WorkingStream | null;
  readonly categories: readonly EditorCategory[];
  readonly review: ReviewRecord | undefined;
}

export function VideoReadView({
  base,
  draft,
  video,
  stream = null,
  categories,
  review,
}: VideoReadViewProps) {
  const { t } = useTranslation('video');
  const { t: ta } = useTranslation('articles');
  const title = draft.title.trim().length === 0 ? t('list.untitled') : draft.title;
  const filedUnder = draft.categories.map(
    (code) => categories.find((category) => category.code === code)?.label ?? code,
  );
  const findings = review?.findings ?? [];
  const about = stream === null ? t('readView.about') : t('stream.about');
  return (
    <Stack gap="lg">
      <div className={editorClasses.top}>
        <div className={editorClasses.crumbs}>
          <InAppAnchor href={VIDEOS_HREF} className={editorClasses.crumbLink} underline="hover">
            {t('editor.crumb')}
          </InAppAnchor>
          <span aria-hidden="true">›</span>
          <h1 className={editorClasses.crumbTitle}>{title}</h1>
        </div>
        <div className={editorClasses.actions}>
          <StatusPill status={base.status} />
        </div>
      </div>

      <Text size="sm" role="status">
        {base.status === 'in_review' ? t('readView.inReview') : t('readView.locked')}
      </Text>

      <div className={editorClasses.layout}>
        <div className={editorClasses.grid}>
          <article className={editorClasses.canvas} aria-label={t('readView.label')}>
            <VideoBlock
              articleId={base.articleId}
              state={video}
              disabled
              stream={stream !== null}
            />
            <h2>{title}</h2>
            {draft.subtitle.trim().length === 0 ? null : <Text c="dimmed">{draft.subtitle}</Text>}
            <Stack gap="sm" mt="md">
              {draft.blocks.map((block) =>
                block.kind === 'text' || block.kind === 'quote' ? (
                  <ReadOnlyText key={block.blockId} block={block} />
                ) : block.kind === 'divider' ? (
                  <hr key={block.blockId} />
                ) : null,
              )}
            </Stack>
          </article>

          <aside className={editorClasses.panel} aria-label={about}>
            <Stack gap="md">
              <Title order={2} size="h5">
                {about}
              </Title>
              {stream === null ? null : <StreamWhen stream={stream} />}
              {base.coverUrl === null ? null : (
                <div
                  className={classes.coverPreview}
                  data-orientation={video.video?.orientation ?? 'horizontal'}
                >
                  <img
                    className={classes.coverImage}
                    src={emulatorImageUrl(base.coverUrl)}
                    onError={servedUrlOnError(base.coverUrl)}
                    alt={t('cover.previewAlt')}
                  />
                </div>
              )}
              {filedUnder.length === 0 ? null : (
                <Text size="sm">
                  {ta('editor.readView.category')}: {filedUnder.join(', ')}
                </Text>
              )}
              {review === undefined || findings.length === 0 ? null : (
                <ReviewNotes
                  review={review}
                  blocks={draft.blocks}
                  onResolve={() => undefined}
                  resolvingOrdinal={undefined}
                  disabled
                />
              )}
              {base.status === 'published' ? (
                <ArchiveThisArticle articleId={base.articleId} />
              ) : null}
            </Stack>
          </aside>
        </div>
      </div>
    </Stack>
  );
}

/** "Starts …", "Ends …" on the author's clock, and which clock that is; a time not set is left out. */
function StreamWhen({ stream }: { stream: WorkingStream }) {
  const { t } = useTranslation('video');
  if (stream.startsAt === null && stream.endsAt === null) {
    return null;
  }
  return (
    <Stack gap={2}>
      {stream.startsAt === null ? null : (
        <Text size="sm">{t('stream.readStarts', { when: formatInstant(stream.startsAt) })}</Text>
      )}
      {stream.endsAt === null ? null : (
        <Text size="sm">{t('stream.readEnds', { when: formatInstant(stream.endsAt) })}</Text>
      )}
      <Text size="xs" c="dimmed">
        {/* Not a clock read: the zone the browser is set to, which `formatInstant` writes in. */}
        {t('stream.timesIn', { zone: Intl.DateTimeFormat().resolvedOptions().timeZone })}
      </Text>
    </Stack>
  );
}
